import React, { createContext, useState, useCallback, type ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { workScheduleService } from '../../../shared/services/timeclock/timeclockService';
import type { WorkSchedule, WorkScheduleFormData, WorkScheduleFilters } from '../types/timeclock.types';

export interface WorkScheduleContextData {
  // Listagem
  workSchedules: WorkSchedule[];
  isLoading: boolean;
  error: string | null;
  handleCreate: () => void;
  handleView: (workSchedule: WorkSchedule) => void;
  handleCloseError: () => void;

  // Modal de Delete
  isDeleteModalOpen: boolean;
  openDeleteModal: (workSchedule: WorkSchedule) => void;
  closeDeleteModal: () => void;
  handleDeleteWorkSchedule: () => void;
  isDeleting: boolean;

  // Form
  workScheduleData: WorkSchedule | null;
  isDataLoading: boolean;
  createWorkSchedule: (data: WorkScheduleFormData) => Promise<void>;
  updateWorkSchedule: (id: string, data: Partial<WorkScheduleFormData>) => Promise<void>;
  isSubmitting: boolean;
  setError: (error: string | null) => void;
  navigateToEdit: (id: string) => void;
}

interface WorkScheduleProviderProps {
  children: ReactNode;
}

export const WorkScheduleContext = createContext<WorkScheduleContextData>({} as WorkScheduleContextData);

export function WorkScheduleProvider({ children }: WorkScheduleProviderProps) {
  const navigate = useNavigate();
  const { id } = useParams();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedWorkSchedule, setSelectedWorkSchedule] = useState<WorkSchedule | null>(null);

  // Query para listar jornadas de trabalho
  const { data: workSchedules = [], isLoading } = useQuery({
    queryKey: ['work-schedules'],
    queryFn: async () => {
      try {
        const response = await workScheduleService.list();

        return response || [];
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao buscar jornadas de trabalho';
        setError(errorMessage);
        return [];
      }
    }
  });

  // Query para buscar dados da jornada no modo de edição
  const { data: workScheduleData = null, isLoading: isDataLoading } = useQuery({
    queryKey: ['work-schedule', id],
    queryFn: async () => {
      if (!id) return null;
      try {
        const response = await workScheduleService.get(id);
        return response;
      } catch (error: any) {
        const errorMessage = error.response?.data?.message || error.message || 'Erro ao buscar jornada de trabalho';
        setError(errorMessage);
        return null;
      }
    },
    enabled: !!id,
  });

  // Mutation para criar jornada
  const createMutation = useMutation({
    mutationFn: async (data: WorkScheduleFormData) => {
      await workScheduleService.create(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-schedules'] });
      navigate('/timeclock');
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao criar jornada de trabalho';
      setError(errorMessage);
      throw error;
    }
  });

  // Mutation para atualizar jornada
  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<WorkScheduleFormData> }) => {
      await workScheduleService.update(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-schedules'] });
      queryClient.invalidateQueries({ queryKey: ['work-schedule', id] });
      navigate('/timeclock');
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao atualizar jornada de trabalho';
      setError(errorMessage);
      throw error;
    }
  });

  // Mutation para deletar jornada
  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await workScheduleService.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['work-schedules'] });
      setIsDeleteModalOpen(false);
      setSelectedWorkSchedule(null);
    },
    onError: (error: any) => {
      const errorMessage = error.response?.data?.message || error.message || 'Erro ao deletar jornada de trabalho';
      setError(errorMessage);
      throw error;
    }
  });

  const handleCreate = useCallback(() => {
    navigate('/timeclock/new');
  }, [navigate]);

  const handleView = useCallback((workSchedule: WorkSchedule) => {
    navigate(`/timeclock/${workSchedule.id}`);
  }, [navigate]);

  const handleCloseError = useCallback(() => {
    setError(null);
  }, []);

  const openDeleteModal = useCallback((workSchedule: WorkSchedule) => {
    setSelectedWorkSchedule(workSchedule);
    setIsDeleteModalOpen(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setIsDeleteModalOpen(false);
    setSelectedWorkSchedule(null);
  }, []);

  const handleDeleteWorkSchedule = useCallback(() => {
    if (selectedWorkSchedule) {
      deleteMutation.mutate(selectedWorkSchedule.id);
    }
  }, [selectedWorkSchedule, deleteMutation]);

  const createWorkSchedule = useCallback(async (data: WorkScheduleFormData) => {
    await createMutation.mutateAsync(data);
  }, [createMutation]);

  const updateWorkSchedule = useCallback(async (id: string, data: Partial<WorkScheduleFormData>) => {
    await updateMutation.mutateAsync({ id, data });
  }, [updateMutation]);

  const navigateToEdit = useCallback((id: string) => {
    navigate(`/timeclock/${id}/edit`);
  }, [navigate]);

  const contextValue: WorkScheduleContextData = {
    workSchedules,
    isLoading,
    error,
    handleCreate,
    handleView,
    handleCloseError,
    isDeleteModalOpen,
    openDeleteModal,
    closeDeleteModal,
    handleDeleteWorkSchedule,
    isDeleting: deleteMutation.isPending,
    workScheduleData,
    isDataLoading,
    createWorkSchedule,
    updateWorkSchedule,
    isSubmitting: createMutation.isPending || updateMutation.isPending,
    setError,
    navigateToEdit,
  };

  return (
    <WorkScheduleContext.Provider value={contextValue}>
      {children}
    </WorkScheduleContext.Provider>
  );
} 